/*
 * nvidia-promo.js
 * ---------------
 * Progressive enhancement for the Nvidia campaign template.
 *   - before / after comparison sliders
 *   - click-to-load video (nothing from YouTube or Vimeo is requested
 *     until someone presses play)
 *   - optional jump navigation
 *
 * Everything here is optional. With JavaScript switched off the page still
 * shows all of its content, the comparison sits at its default split and the
 * video falls back to a normal link.
 *
 * Safe to run more than once: every component is marked when it is set up,
 * so theme editor section reloads never attach duplicate listeners.
 */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ---------------------------------------------------------------- compare */
  function initCompare(el) {
    if (el.getAttribute('data-np-ready')) return;
    var range = el.querySelector('[data-np-compare-range]');
    if (!range) return;
    el.setAttribute('data-np-ready', 'true');

    var update = function () {
      el.style.setProperty('--np-compare-pos', range.value + '%');
    };
    range.addEventListener('input', update);
    range.addEventListener('change', update);
    update();
    el.setAttribute('data-np-interactive', 'true');
  }

  /* ------------------------------------------------------------------ video */
  function initVideo(el) {
    if (el.getAttribute('data-np-ready')) return;
    var button = el.querySelector('[data-np-video-play]');
    var holder = el.querySelector('[data-np-video-holder]');
    if (!button || !holder) return;
    el.setAttribute('data-np-ready', 'true');

    button.addEventListener('click', function () {
      var file = el.getAttribute('data-np-video-file');
      var embed = el.getAttribute('data-np-video-embed');
      var title = el.getAttribute('data-np-video-title') || 'Campaign video';
      var node;

      if (file) {
        node = document.createElement('video');
        node.src = file;
        node.controls = true;
        node.autoplay = true;
        node.setAttribute('playsinline', '');
        node.setAttribute('title', title);
      } else if (embed) {
        node = document.createElement('iframe');
        node.src = embed;
        node.setAttribute('title', title);
        node.setAttribute('frameborder', '0');
        node.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        );
        node.setAttribute('allowfullscreen', '');
      }

      if (!node) return;
      holder.innerHTML = '';
      holder.appendChild(node);
      el.className += ' is-playing';
      if (node.focus) node.focus();
    });
  }

  /* -------------------------------------------------------------------- nav */
  function initNav(el) {
    if (el.getAttribute('data-np-ready')) return;
    el.setAttribute('data-np-ready', 'true');

    var links = el.querySelectorAll('[data-np-nav-link]');
    if (!links.length) return;

    each(links, function (link) {
      link.addEventListener('click', function (event) {
        var id = (link.getAttribute('href') || '').replace('#', '');
        var target = id ? document.getElementById(id) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          if (!entry.isIntersecting) return;
          each(links, function (link) {
            var active = link.getAttribute('href') === '#' + entry.target.id;
            link.className = link.className.replace(' is-active', '');
            link.setAttribute('aria-current', active ? 'true' : 'false');
            if (active) link.className += ' is-active';
          });
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    each(links, function (link) {
      var id = (link.getAttribute('href') || '').replace('#', '');
      var target = id ? document.getElementById(id) : null;
      if (target) observer.observe(target);
    });
  }

  /* ------------------------------------------------------------------- boot */
  function init(scope) {
    var root = scope || document;
    if (!root.querySelectorAll) return;
    each(root.querySelectorAll('[data-np-compare]'), initCompare);
    each(root.querySelectorAll('[data-np-video]'), initVideo);
    each(root.querySelectorAll('[data-np-nav]'), initNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
